import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AdminStatsResponse, DecisionAudit } from '@kkp-fraud/shared';
import { api } from '../api';
import { useShell } from '../components/AppLayout';
import { When } from '../components/When';
import { LoadError } from '../components/LoadError';
import { PageHead, Tip } from '../components/PageHead';
import { Sparkline } from '../components/Sparkline';
import { DEMO, demoDependencies } from '../demo';
import { dateTime, ms, num, timeOfDay } from '../format';
import { fill } from '../components/Interpolated';
import { t } from '../i18n';
import { dependenciesFromReadiness, minuteSeries, throughput } from '../monitorData';

/** What `GET /readyz` answers today. */
interface Readiness {
  status: string;
  stateStore: string;
  stateStoreOk: boolean;
  rulesLoaded: boolean;
  ruleSetVersion: string;
  rulesSource: string;
  loadedAt: string;
  shadowMode: boolean;
  /** 2c rule sets — every enabled set the engine tried to load (absent on an engine before 2c). */
  ruleSets?: { id: string; version: string | null; loaded: boolean; error?: string }[];
}

/** The admin service clamps `GET /api/decisions` at this many rows. */
const DECISION_WINDOW = 1000;

/**
 * Monitor — the ops view, from `/readyz`, `/admin/stats` and the recent decisions.
 *
 * The mockup's latency histogram is not reproduced: `/admin/stats` returns p50/p95/p99, not
 * buckets, so a histogram here would be drawn from numbers nobody measured. Percentiles are
 * shown as percentiles. Throughput and the per-minute lines are counted from the decision
 * rows themselves; the dependency list names only what `/readyz` vouches for (the demo data
 * layer lists the platform's five).
 */
export function MonitorScreen() {
  const { settings } = useShell();
  const [ready, setReady] = useState<Readiness | null>(null);
  const [readyError, setReadyError] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [auto, setAuto] = useState(false);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [rows, setRows] = useState<DecisionAudit[]>([]);
  const [rttMs, setRttMs] = useState<number | null>(null);
  /** Only the newest readiness answer is shown, whatever order they come back in. */
  const readySeq = useRef(0);
  /** One stats request at a time: on Azure it takes ~12 s, longer than the 5 s auto-refresh. */
  const statsInFlight = useRef(false);

  /**
   * Three independent reads. `/readyz` answers in milliseconds and `/admin/stats` can take
   * many seconds, so neither waits for the other: readiness and dependency health render as
   * soon as `/readyz` answers, the latency card shows its own loading state, and a failure
   * of one is reported in its own card only.
   */
  const refresh = useCallback(() => {
    const seq = ++readySeq.current;
    const started = performance.now();
    api.ready(settings).then(
      (r) => {
        if (seq !== readySeq.current) return;
        setRttMs(performance.now() - started);
        setReady(r as Readiness);
        setReadyError(null);
        setCheckedAt(new Date().toISOString());
      },
      (e: unknown) => {
        if (seq !== readySeq.current) return;
        // Readiness could not be read: nothing it said before is current any more (QA-M-02).
        // The rows read "unknown" and the decision API "down" — never the last good answer.
        setReady(null);
        setRttMs(null);
        setReadyError((e as Error).message);
        setCheckedAt(new Date().toISOString());
      },
    );

    if (!statsInFlight.current) {
      statsInFlight.current = true;
      setStatsLoading(true);
      api
        .stats(settings)
        .then(
          (s) => {
            setStats(s);
            setStatsError(null);
          },
          (e: unknown) => {
            // The same for the percentiles: no figures rather than the last good ones under an error.
            setStats(null);
            setStatsError((e as Error).message);
          },
        )
        .finally(() => {
          statsInFlight.current = false;
          setStatsLoading(false);
        });
    }

    // The decision rows only feed the throughput card; their failure must not blank anything.
    api.decisions(settings, DECISION_WINDOW).then(
      (d) => setRows(d),
      () => undefined,
    );
  }, [settings.baseUrl]);

  const now = checkedAt ? Date.parse(checkedAt) : Date.now();
  const series = useMemo(() => minuteSeries(rows, now), [rows, now]);
  const tps = useMemo(() => throughput(rows, now), [rows, now]);
  /** `/readyz` has not answered yet — nothing is known, so nothing is shown as down. */
  const readyPending = !DEMO && ready === null && readyError === null;
  const deps = useMemo(() => (DEMO ? demoDependencies(now) : dependenciesFromReadiness(ready, rttMs)), [ready, rttMs, now]);
  const lastHour = series.counts.reduce((a, b) => a + b, 0);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!auto) return;
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [auto, refresh]);

  const dot = (ok: boolean, okLabel: string, badLabel: string) => (
    <span className={`chip ${ok ? 'ok' : 'bad'}`}>{ok ? okLabel : badLabel}</span>
  );
  /** A readiness value `/readyz` could not tell us: "unknown" once a read failed, "—" before the first answer. */
  const unknown = readyError ? <span className="chip mute">{t('monitor.status.unknown')}</span> : '—';

  return (
    <>
      <PageHead title={t('screen.monitor.title')} lead={t('screen.monitor.console.lead')} />

      <section className="card">
        <header className="card-head">
          <h2>{t('monitor.readiness.title')}</h2>
          {checkedAt && <span className="small muted">{t('monitor.checked.at', { at: dateTime(checkedAt) })}</span>}
          <span className="spacer" />
          <label className="check small">
            <input
              type="checkbox"
              className="toggle"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
              aria-label={t('monitor.autorefresh.a11y')}
            />
            {t('monitor.autorefresh.label')}
          </label>
          <button type="button" className="btn" onClick={refresh}>
            {t('btn.refresh.label')}
          </button>
        </header>
        <div className="card-body stack">
          {readyError && <LoadError message={t('monitor.error.readiness')} detail={readyError} testId="monitor-error" />}
          {/* UX review P2-14: a label/value table no wider than ~720 px — each value next to its label. */}
          <div className="tbl-wrap compact">
            <table>
              <tbody>
                <tr>
                  <td>{t('monitor.rules_loaded.label')}</td>
                  <td className="right">
                    {ready ? dot(ready.rulesLoaded, t('monitor.rules_loaded.ok'), t('monitor.rules_loaded.bad')) : unknown}
                  </td>
                </tr>
                <tr>
                  <td>
                    {/* The store's name only when readiness named it — no "(—)" while it is unknown. */}
                    {t('monitor.state_store.label')} {ready && <span className="mono small">({ready.stateStore})</span>}
                  </td>
                  <td className="right">
                    {ready ? dot(ready.stateStoreOk, t('monitor.state_store.ok'), t('monitor.state_store.bad')) : unknown}
                  </td>
                </tr>
                <tr>
                  <td>{t('monitor.ruleset_version.label')}</td>
                  <td className="right mono">{ready?.ruleSetVersion ?? '—'}</td>
                </tr>
                <tr>
                  <td>{t('monitor.rules_source.label')}</td>
                  <td className="right mono">{ready?.rulesSource ?? '—'}</td>
                </tr>
                {/* 2c rule sets: one line per set, once there is more than one */}
                {(ready?.ruleSets?.length ?? 0) > 1 &&
                  ready!.ruleSets!.map((s) => (
                    <tr key={s.id} data-testid={`monitor-set-${s.id}`}>
                      <td>
                        {t('rulesets.monitor.set.label')} <span className="mono small">({s.id})</span>
                      </td>
                      <td className="right" title={s.error}>
                        {dot(s.loaded, t('rulesets.engine.loaded', { version: s.version ?? '—' }), t('rulesets.engine.refused'))}
                      </td>
                    </tr>
                  ))}
                <tr>
                  <td>{t('monitor.loaded_at.label')}</td>
                  <td className="right small">
                    <When iso={ready?.loadedAt} />
                  </td>
                </tr>
                <tr>
                  <td>{t('monitor.shadow.label')}</td>
                  <td className="right">
                    {ready ? (
                      ready.shadowMode ? (
                        <span className="chip warn">{t('monitor.shadow.on')}</span>
                      ) : (
                        <span className="chip">{t('monitor.shadow.off')}</span>
                      )
                    ) : (
                      unknown
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="card" data-testid="monitor-latency">
        <header className="card-head">
          <h2>{t('monitor.percentiles.title')}</h2>
          {stats && <span className="chip mute">{t('monitor.percentiles.window', { n: num(stats.total) })}</span>}
          {statsLoading && (
            <span className="small muted" role="status" data-testid="monitor-stats-loading">
              {t('monitor.stats.loading')}
            </span>
          )}
          <Tip>
            {fill(t('monitor.percentiles.note'), {
              '{metric}': <span className="mono">service_latency_ms</span>,
              '{sample}': <span className="mono">STATS_SAMPLE_SIZE</span>,
              '{flag}': <span className="mono">{t('flag.degraded.chip')}</span>,
            })}
          </Tip>
        </header>
        <div className="card-body stack">
          {statsError && <LoadError message={t('monitor.error.stats')} detail={statsError} testId="monitor-stats-error" />}
          <div className="grid g4">
            <div className="kpi">
              <div className="lbl">p50</div>
              <div className="val sm">{stats ? ms(stats.p50Ms) : '—'}</div>
            </div>
            <div className="kpi">
              <div className="lbl">p95</div>
              <div className="val sm">{stats ? ms(stats.p95Ms) : '—'}</div>
            </div>
            <div className="kpi">
              <div className="lbl">p99</div>
              <div className="val sm">{stats ? ms(stats.p99Ms) : '—'}</div>
            </div>
            <div className="kpi">
              <div className="lbl">{t('flag.degraded.chip')}</div>
              <div className="val">{stats ? num(stats.degraded) : '—'}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="card" data-testid="monitor-throughput">
        <header className="card-head">
          <h2>{t('monitor.throughput.title')}</h2>
          <span className="chip mute">
            {t(lastHour === 1 ? 'monitor.throughput.window.one' : 'monitor.throughput.window.other', { n: num(lastHour) })}
          </span>
        </header>
        <div className="card-body stack">
          <div className="grid g4">
            <div className="kpi">
              <div className="lbl">{t('monitor.tps.now')}</div>
              <div className="val sm" data-testid="monitor-tps-now">{rows.length ? num(tps.tpsNow, 2) : '—'}</div>
            </div>
            <div className="kpi">
              <div className="lbl">{t('monitor.tps.peak')}</div>
              <div className="val sm" data-testid="monitor-tps-peak">{rows.length ? num(tps.tpsPeak, 2) : '—'}</div>
              {tps.peakAt && <div className="sub">{t('monitor.tps.peak.at', { at: timeOfDay(tps.peakAt) })}</div>}
            </div>
            <div className="kpi">
              <div className="lbl">{t('monitor.minute.last')}</div>
              <div className="val sm">{rows.length ? num(series.counts[series.counts.length - 1] ?? 0) : '—'}</div>
            </div>
            <div className="kpi">
              <div className="lbl">{t('monitor.minute.p95')}</div>
              <div className="val sm">{ms([...series.p95].reverse().find((v) => v !== null) ?? null)}</div>
            </div>
          </div>
          <div className="grid g2">
            <MinuteTrend
              label={t('monitor.spark.decisions')}
              values={series.counts}
              empty={lastHour === 0}
              format={(n) => num(n)}
              testId="monitor-spark-decisions"
            />
            <MinuteTrend
              label={t('monitor.spark.p95')}
              values={series.p95}
              empty={!series.p95.some((v) => v !== null && Number.isFinite(v))}
              format={(n) => ms(n)}
              testId="monitor-spark-p95"
            />
          </div>
        </div>
      </section>

      <section className="card" data-testid="monitor-deps">
        <header className="card-head">
          <h2>{t('monitor.deps.title')}</h2>
        </header>
        <div className="tbl-wrap compact">
          <table>
            <tbody>
              {deps.map((d) => (
                <tr key={d.id} data-testid={`dep-${d.id}`}>
                  <td>
                    {d.name} <span className="small muted">{t(`monitor.dep.${d.id}.role`)}</span>
                  </td>
                  <td className="right">
                    {readyPending ? '—' : dot(d.up, t('monitor.state_store.ok'), t('monitor.state_store.bad'))}
                  </td>
                  <td className="right mono small" style={{ width: 110 }}>
                    {ms(d.latencyMs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/**
 * One per-minute line: the latest value, the line, and its range. With nothing to draw (no
 * transaction in the last 60 minutes) it says so instead of a flat line or an empty frame
 * (UX review P2-15).
 */
function MinuteTrend({
  label,
  values,
  empty,
  format,
  testId,
}: {
  label: string;
  values: readonly (number | null)[];
  empty: boolean;
  format: (n: number) => string;
  testId: string;
}) {
  const finite = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (empty) {
    return (
      <div className="trendfig">
        <div className="trendhead">
          <span className="lbl">{label}</span>
        </div>
        <div className="empty trend-empty" role="status" data-testid={`${testId}-empty`}>
          {t('monitor.spark.empty')}
        </div>
      </div>
    );
  }
  return (
    <div className="trendfig">
      <div className="trendhead">
        <span className="lbl">{label}</span>
      </div>
      <Sparkline values={values} label={label} testId={testId} />
      {finite.length > 0 && (
        <div className="small muted mono">
          {t('dash.trend.range', { min: format(Math.min(...finite)), max: format(Math.max(...finite)) })}
        </div>
      )}
    </div>
  );
}
